import type { ButtonSchema, ButtonSchemaWithId } from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { PartialButton } from "~/providers/button-provider";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { useButtonStore } from "~/stores/use-button-store";
import { usePermissionManager } from "./use-permission-manager";

export const useButtonManager = () => {
  const { t } = useTranslation("common");

  const { hasPermission } = usePermissionManager();

  const { handleButtonUpdate, handleButtonDelete } = useButtonUpdates();

  const { showDialog } = useAlertStore();

  const { setButtonDialogOpen, setSelectedButton } = useButtonStore();

  const { mutate: createButton } = api.button.createButton.useMutation({
    async onSuccess(result) {
      toast.success(t("create_item_success", { item: t("Button.button") }));
      await handleButtonUpdate(result);
    },
    onError() {
      toast.error(t("create_item_error", { item: t("Button.button") }));
    },
  });

  const { mutate: updateButton } = api.button.updateButton.useMutation({
    async onSuccess(_, variables) {
      toast.success(t("update_item_success", { item: "button" }));
      await handleButtonUpdate(variables.id);
    },
    onError() {
      toast.error(t("update_item_error", { item: "button" }));
    },
  });

  const { mutate: deleteButton } = api.button.deleteButton.useMutation({
    onSuccess(_, variables) {
      toast.success(
        capitalize(t("delete_item_success", { item: t("Button.button") })),
      );
      handleButtonDelete(variables.id);
    },
    onError() {
      toast.error(
        capitalize(t("delete_item_fail", { item: t("Button.button") })),
      );
    },
  });

  const createButtonHandler = async (button: z.infer<typeof ButtonSchema>) => {
    return new Promise<boolean>((resolve) => {
      createButton(button, {
        onSuccess() {
          return resolve(true);
        },
        onError() {
          return resolve(false);
        },
      });
    });
  };

  const updateButtonHandler = async (
    button: z.infer<typeof ButtonSchemaWithId>,
  ) => {
    return new Promise<boolean>((resolve) => {
      updateButton(button, {
        onSuccess() {
          return resolve(true);
        },
        onError() {
          return resolve(false);
        },
      });
    });
  };

  const deleteButtonHandler = async (id: string) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.delete_button,
      label: t("Permissions.delete_button_label"),
    });

    if (!permission) return false;

    const result = await showDialog({
      title: t("Button.before_delete_title"),
      message: t("Button.before_delete_message"),
      variant: "destructive",
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    if (result === "cancel") return false;

    return new Promise<boolean>((resolve) => {
      deleteButton(
        { id },
        {
          onSuccess() {
            return resolve(true);
          },
          onError() {
            return resolve(false);
          },
        },
      );
    });
  };

  const openButtonDialog = (button?: PartialButton) => {
    if (button) {
      setSelectedButton(button);
    }
    setButtonDialogOpen(true);
  };

  const clearSelectedButton = () => {
    setSelectedButton(undefined);
  };

  return {
    createButton: createButtonHandler,
    updateButton: updateButtonHandler,
    deleteButton: deleteButtonHandler,
    setSelectedButton: setSelectedButton,
    clearSelectedButton,
    openButtonDialog,
  };
};

export const useButtonUpdates = () => {
  const utils = api.useUtils();

  const handleButtonUpdate = useCallback(
    async (buttonId: string) => {
      const result = await utils.button.getById.fetch(buttonId);

      void utils.button.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [utils.button.getAll, utils.button.getById],
  );

  const handleButtonDelete = useCallback(
    (buttonId: string) => {
      void utils.button.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== buttonId);
      });
    },
    [utils.button.getAll],
  );

  return {
    handleButtonUpdate,
    handleButtonDelete,
  };
};
